import React from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTheme } from "../../contexts/ThemeContext";

export const CategoryCardSkeleton = ({ count = 4 }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const baseColor = isDark ? "#1e1f3a" : "#e2e8f0";
  const highlightColor = isDark ? "#252642" : "#f1f5f9";

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "12px",
            padding: "20px",
            display: "flex",
            alignItems: "center",
            gap: "24px",
            marginBottom: "16px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Icon skeleton */}
          <Skeleton
            circle
            width={46}
            height={46}
            baseColor={baseColor}
            highlightColor={highlightColor}
          />
          {/* Title skeleton */}
          <div style={{ flex: 1 }}>
            <Skeleton
              width="60%"
              height={28}
              borderRadius={8}
              baseColor={baseColor}
              highlightColor={highlightColor}
            />
          </div>
          {/* Stats skeleton */}
          <div style={{ display: "flex", gap: "24px" }}>
            <Skeleton
              width={75}
              height={24}
              borderRadius={6}
              baseColor="#ECB236"
              highlightColor="#f0d78c"
            />
            <Skeleton
              width={75}
              height={24}
              borderRadius={6}
              baseColor="#ECB236"
              highlightColor="#f0d78c"
            />
          </div>
        </div>
      ))}
    </>
  );
};

export const DiscussionCardSkeleton = ({ count = 4 }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const baseColor = isDark ? "#1e1f3a" : "#e2e8f0";
  const highlightColor = isDark ? "#252642" : "#f1f5f9";

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "12px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", gap: "16px" }}>
            {/* Avatar skeleton */}
            <Skeleton
              circle
              width={48}
              height={48}
              baseColor={baseColor}
              highlightColor={highlightColor}
            />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Meta row skeleton */}
              <div style={{ display: "flex", gap: "8px" }}>
                <Skeleton
                  width={80}
                  height={20}
                  borderRadius={8}
                  baseColor={baseColor}
                  highlightColor={highlightColor}
                />
                <Skeleton
                  width={120}
                  height={20}
                  borderRadius={8}
                  baseColor={baseColor}
                  highlightColor={highlightColor}
                />
                <Skeleton
                  width={80}
                  height={20}
                  borderRadius={8}
                  baseColor={baseColor}
                  highlightColor={highlightColor}
                />
              </div>
              {/* Title skeleton */}
              <Skeleton
                width="80%"
                height={28}
                borderRadius={8}
                baseColor={baseColor}
                highlightColor={highlightColor}
              />
            </div>
          </div>
          {/* Footer stats skeleton */}
          <div style={{ display: "flex", gap: "20px", marginTop: "12px", paddingLeft: "64px" }}>
            <Skeleton
              width={100}
              height={18}
              borderRadius={8}
              baseColor={baseColor}
              highlightColor={highlightColor}
            />
            <Skeleton
              width={80}
              height={18}
              borderRadius={8}
              baseColor={baseColor}
              highlightColor={highlightColor}
            />
          </div>
        </div>
      ))}
    </>
  );
};

export const LatestPostsSidebarSkeleton = ({ count = 4 }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const baseColor = isDark ? "#1e1f3a" : "#e2e8f0";
  const highlightColor = isDark ? "#252642" : "#f1f5f9";

  return (
    <div
      style={{
        backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: "12px",
        padding: "20px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header skeleton */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <Skeleton
          circle
          width={22}
          height={22}
          baseColor={baseColor}
          highlightColor={highlightColor}
        />
        <Skeleton
          width={180}
          height={22}
          borderRadius={8}
          baseColor={baseColor}
          highlightColor={highlightColor}
        />
      </div>

      {/* Post cards skeleton */}
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            padding: "14px",
            borderRadius: "12px",
            backgroundColor: isDark ? "#1e1f3a" : "#f8fafc",
            border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
            marginBottom: "16px",
          }}
        >
          <Skeleton
            width="90%"
            height={18}
            borderRadius={6}
            baseColor={baseColor}
            highlightColor={highlightColor}
            style={{ marginBottom: "8px" }}
          />
          <Skeleton
            width="60%"
            height={14}
            borderRadius={6}
            baseColor={baseColor}
            highlightColor={highlightColor}
            style={{ marginBottom: "8px" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Skeleton
              width={60}
              height={12}
              borderRadius={6}
              baseColor={baseColor}
              highlightColor={highlightColor}
            />
            <Skeleton
              width={50}
              height={12}
              borderRadius={6}
              baseColor={baseColor}
              highlightColor={highlightColor}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export const PostCardSkeleton = ({ count = 3 }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const baseColor = isDark ? "#1e1f3a" : "#e2e8f0";
  const highlightColor = isDark ? "#252642" : "#f1f5f9";

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "16px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", gap: "16px" }}>
            {/* Avatar skeleton */}
            <Skeleton
              circle
              width={48}
              height={48}
              baseColor={baseColor}
              highlightColor={highlightColor}
            />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Author name skeleton */}
              <Skeleton
                width={140}
                height={18}
                borderRadius={8}
                baseColor={baseColor}
                highlightColor={highlightColor}
              />
              {/* Date skeleton */}
              <Skeleton
                width={100}
                height={14}
                borderRadius={8}
                baseColor={baseColor}
                highlightColor={highlightColor}
              />
              {/* Content lines skeleton */}
              <Skeleton
                width="100%"
                height={16}
                borderRadius={8}
                baseColor={baseColor}
                highlightColor={highlightColor}
              />
              <Skeleton
                width="90%"
                height={16}
                borderRadius={8}
                baseColor={baseColor}
                highlightColor={highlightColor}
              />
              <Skeleton
                width="70%"
                height={16}
                borderRadius={8}
                baseColor={baseColor}
                highlightColor={highlightColor}
              />
            </div>
          </div>
        </div>
      ))}
    </>
  );
};
